import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import BusinessPage from "./pages/BusinessPage";
import PublicBusinessPage from "./pages/PublicBusinessPage";
import AdminDashboard from "./pages/AdminDashboard";
import BookingSuccess from "./pages/BookingSuccess";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import FoundingMembers from "./pages/FoundingMembers";
import CookieConsent from "./components/CookieConsent";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-appbg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Admin Route Component - requires platform_admin role
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-appbg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (user.role !== 'platform_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route Component (redirect to appropriate dashboard if logged in, unless there's a redirect param)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-appbg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    // Check for redirect URL in URL params or localStorage
    const location = window.location;
    const searchParams = new URLSearchParams(location.search);
    const urlRedirect = searchParams.get('redirect');
    const storedRedirect = localStorage.getItem('calendrax_redirect');
    const redirectUrl = urlRedirect || storedRedirect;
    
    // Clear the stored redirect
    if (storedRedirect) {
      localStorage.removeItem('calendrax_redirect');
    }
    
    if (redirectUrl) {
      return <Navigate to={redirectUrl} replace />;
    }
    
    // No redirect param - go to appropriate dashboard
    if (user.role === 'platform_admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public landing page - shows businesses */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/signup" 
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        } 
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      {/* Public business preview page */}
      <Route path="/business/:businessId" element={<PublicBusinessPage />} />
      {/* Protected booking page */}
      <Route 
        path="/book/:businessId" 
        element={
          <ProtectedRoute>
            <BusinessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/booking-success" 
        element={
          <ProtectedRoute>
            <BookingSuccess />
          </ProtectedRoute>
        } 
      />
      
      {/* Public pages */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/founding-members" element={<FoundingMembers />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
